import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api, { getApiErrorMessage, getApiErrorPayload } from '../api';
import {
    isAllowedRegistrationEmail,
    REGISTRATION_EMAIL_DOMAIN_HINT,
    REGISTRATION_EMAIL_DOMAIN_ERROR,
} from '../utils/emailDomain';
import { getStoredInvite } from '../utils/invite';
import './Login.css';

const Login = () => {
    const navigate = useNavigate();
    const initialSearchParams = new URLSearchParams(window.location.search);
    const hasMagicToken = initialSearchParams.has('magic_token');

    const [activeTab, setActiveTab] = useState('email');
    const [telegramWebAuthEnabled, setTelegramWebAuthEnabled] = useState(true);
    const [webAppUrl, setWebAppUrl] = useState(window.location.origin);
    const [authConfigLoaded, setAuthConfigLoaded] = useState(false);
    const [telegramDisabledNotice, setTelegramDisabledNotice] = useState(
        hasMagicToken ? '' : ''
    );

    const [loginToken, setLoginToken] = useState(null);
    const [botUsername, setBotUsername] = useState(null);
    const [tgStatus, setTgStatus] = useState('loading');
    const [pollingCount, setPollingCount] = useState(0);
    const pollInterval = useRef(null);
    
    // ?mode=register — ссылки «Попробовать бесплатно» с сайта svodx.ru сразу открывают регистрацию
    const [isLoginMode, setIsLoginMode] = useState(initialSearchParams.get('mode') !== 'register');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [emailError, setEmailError] = useState('');
    const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
    const [isEmailLoading, setIsEmailLoading] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [agreedToNewsletter, setAgreedToNewsletter] = useState(false);
    const [invite, setInvite] = useState(null);
    const inviteToken = initialSearchParams.get('invite') || getStoredInvite();

    // Приглашение: показываем, что оно даёт, открываем регистрацию и подставляем почту
    useEffect(() => {
        if (!inviteToken) return;
        api.get(`/invites/${encodeURIComponent(inviteToken)}`)
            .then((res) => {
                setInvite(res.data);
                if (res.data.valid) {
                    setIsLoginMode(false);
                    if (res.data.email) setEmail(res.data.email);
                }
            })
            .catch(() => setInvite(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const invitedEmail = invite?.valid && invite.email && email.trim().toLowerCase() === invite.email;

    useEffect(() => {
        const loadAuthConfig = async () => {
            try {
                const res = await api.get('/auth/config');
                const enabled = res.data.telegram_web_auth_enabled !== false;
                setTelegramWebAuthEnabled(enabled);
                if (res.data.web_app_url) {
                    setWebAppUrl(res.data.web_app_url);
                }
                if (!enabled) {
                    setActiveTab('email');
                    if (hasMagicToken) {
                        setTelegramDisabledNotice(
                            'Функционал организатора в Telegram отключён. Войдите или зарегистрируйтесь в веб-версии по email.'
                        );
                    }
                } else if (hasMagicToken) {
                    setActiveTab('telegram');
                }
            } catch (err) {
                console.error('Failed to load auth config', err);
                setTelegramWebAuthEnabled(false);
                setActiveTab('email');
            } finally {
                setAuthConfigLoaded(true);
            }
        };
        loadAuthConfig();
    }, [hasMagicToken]);

    useEffect(() => {
        if (!authConfigLoaded || !telegramWebAuthEnabled || activeTab !== 'telegram') return;

        const handleTgAuth = async () => {
            const searchParams = new URLSearchParams(window.location.search);
            const magicToken = searchParams.get('magic_token');

            if (magicToken) {
                try {
                    setTgStatus('loading');
                    const res = await api.post('/auth/exchange_magic_link', { token: magicToken });
                    if (res.data.access_token) {
                        localStorage.setItem('token', res.data.access_token);
                        navigate('/dashboard');
                        return;
                    }
                } catch (err) {
                    console.error('Magic link failed', err);
                    const payload = getApiErrorPayload(err);
                    if (payload.code === 'TELEGRAM_WEB_AUTH_DISABLED') {
                        setTelegramDisabledNotice(payload.message || 'Функционал организатора в Telegram отключён. Используйте веб-версию.');
                        setActiveTab('email');
                    }
                    setTgStatus('error');
                    return;
                }
            }

            if (tgStatus === 'loading') {
                try {
                    const res = await api.post('/auth/init');
                    setLoginToken(res.data.login_token);
                    setBotUsername(res.data.bot_username);
                    setTgStatus('ready');
                } catch (err) {
                    console.error('Failed to init auth', err);
                    const payload = getApiErrorPayload(err);
                    if (payload.code === 'TELEGRAM_WEB_AUTH_DISABLED') {
                        setTelegramDisabledNotice(payload.message || 'Функционал организатора в Telegram отключён. Используйте веб-версию.');
                        setActiveTab('email');
                    }
                    setTgStatus('error');
                }
            }
        };
        handleTgAuth();

        return () => {
            if (pollInterval.current) clearInterval(pollInterval.current);
        };
    }, [activeTab, navigate, tgStatus, authConfigLoaded, telegramWebAuthEnabled]);

    useEffect(() => {
        if (activeTab !== 'telegram' || !loginToken || tgStatus === 'loading' || tgStatus === 'error' || tgStatus === 'success') return;

        setPollingCount(0);

        pollInterval.current = setInterval(async () => {
            try {
                setPollingCount(prev => prev + 1);
                const res = await api.get(`/auth/poll/${loginToken}`);
                if (res.data.status === 'success') {
                    clearInterval(pollInterval.current);
                    localStorage.setItem('token', res.data.access_token);
                    setTgStatus('success');
                    setTimeout(() => navigate('/dashboard'), 500);
                }
            } catch (err) {
                if (err.response && err.response.status !== 404) {
                    console.error('Poll error:', err);
                }
            }
        }, 2000);

        return () => {
            if (pollInterval.current) clearInterval(pollInterval.current);
        };
    }, [loginToken, tgStatus, activeTab, navigate]);

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setIsEmailLoading(true);
        setEmailError('');
        setPendingVerificationEmail('');

        if (!isLoginMode) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                setEmailError('Введите корректный адрес электронной почты.');
                setIsEmailLoading(false);
                return;
            }
            if (!invitedEmail && !isAllowedRegistrationEmail(email)) {
                setEmailError(REGISTRATION_EMAIL_DOMAIN_ERROR);
                setIsEmailLoading(false);
                return;
            }
            if (!agreedToTerms) {
                setEmailError('Необходимо подтвердить согласие с публичной офертой на оказание информационных услуг.');
                setIsEmailLoading(false);
                return;
            }
        }
        
        try {
            if (isLoginMode) {
                const res = await api.post('/auth/login/email', { email, password });
                localStorage.setItem('token', res.data.access_token);
                navigate('/dashboard');
            } else {
                await api.post('/auth/register', { 
                    email, 
                    password,
                    agreed_to_terms: agreedToTerms,
                    agreed_to_newsletter: agreedToNewsletter,
                    invite_token: invite?.valid ? inviteToken : null,
                });
                navigate(`/verify-email?email=${encodeURIComponent(email)}`);
            }
        } catch (err) {
            const payload = getApiErrorPayload(err);
            if (payload.code === 'EMAIL_NOT_VERIFIED') {
                const verifyEmail = payload.email || email;
                setPendingVerificationEmail(verifyEmail);
                setEmailError(getApiErrorMessage(err, 'Email не подтвержден. Подтвердите почту, чтобы продолжить.'));
            } else {
                setEmailError(getApiErrorMessage(err, 'Ошибка авторизации.'));
            }
        } finally {
            setIsEmailLoading(false);
        }
    };

    const deepLink = botUsername ? `https://t.me/${botUsername}?start=login_${loginToken}` : '';
    const isWaiting = tgStatus === 'waiting';

    return (
        <div className="login-page">
            <div className="login-card">
                <h1 className="login-title">Вход в СВОД</h1>
                <p className="login-subtitle">
                    Сервис Визуализации Опросов и Данных
                </p>
                
                {telegramWebAuthEnabled && (
                    <div className="login-tabs">
                        <button
                            type="button"
                            className={`login-tab ${activeTab === 'telegram' ? 'login-tab--active' : ''}`}
                            onClick={() => setActiveTab('telegram')}
                        >
                            Telegram
                        </button>
                        <button
                            type="button"
                            className={`login-tab ${activeTab === 'email' ? 'login-tab--active' : ''}`}
                            onClick={() => setActiveTab('email')}
                        >
                            Email / Пароль
                        </button>
                    </div>
                )}

                {invite && (
                    <div className={`login-invite${invite.valid ? '' : ' login-invite--error'}`} role="status">
                        {invite.valid ? (
                            <>
                                <b>{invite.first_name ? `${invite.first_name}, вас` : 'Вас'} пригласили в СВОД</b>
                                {invite.gift && <span>После регистрации подключится тариф {invite.gift}.</span>}
                                <span>Зарегистрируйтесь{invite.email ? ` с адресом ${invite.email}` : ''} или войдите, если аккаунт уже есть, — приглашение применится автоматически.</span>
                            </>
                        ) : (
                            <span>{invite.error}. Вы можете зарегистрироваться как обычно.</span>
                        )}
                    </div>
                )}

                {telegramDisabledNotice && (
                    <div className="form-error" style={{ marginBottom: 'var(--space-4)' }}>
                        {telegramDisabledNotice}{' '}
                        <a href={`${webAppUrl}/login`} className="login-link">Перейти к регистрации</a>
                    </div>
                )}

                {telegramWebAuthEnabled && activeTab === 'telegram' && (
                    <div>
                        {tgStatus === 'loading' ? (
                            <p>Загрузка...</p>
                        ) : tgStatus === 'error' ? (
                            <p>Ошибка соединения. Обновите страницу.</p>
                        ) : (
                            <>
                                <p className="login-subtitle" style={{ marginBottom: 'var(--space-5)' }}>
                                    {isWaiting
                                        ? 'Ожидание подтверждения в Telegram...'
                                        : 'Сканируйте QR-код или нажмите кнопку для входа'}
                                </p>

                                {isWaiting && (
                                    <div className="login-waiting">
                                        <div className="login-spinner" />
                                        <p className="login-waiting-text">
                                            Нажмите кнопку &quot;Start&quot; в открывшемся боте Telegram
                                        </p>
                                    </div>
                                )}

                                <div className="login-qr-box">
                                    {deepLink && <QRCodeSVG value={deepLink} size={200} />}
                                </div>

                                <a
                                    href={deepLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn--primary"
                                    style={{ width: '100%', marginBottom: 'var(--space-4)' }}
                                    onClick={() => {
                                        if (tgStatus === 'ready') setTgStatus('waiting');
                                    }}
                                >
                                    Войти через Telegram Bot
                                </a>

                                <p className="login-helper">
                                    {isWaiting
                                        ? `Ожидание подтверждения... (${pollingCount} попыток)`
                                        : 'Отсканируйте QR, нажмите Start в боте — вход произойдёт автоматически'}
                                </p>
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'email' && (
                    <div className="login-email-panel">
                        <div className="login-toggle-row">
                            <button 
                                type="button"
                                className={`login-toggle ${isLoginMode ? 'login-toggle--active' : ''}`}
                                onClick={() => { setIsLoginMode(true); setEmailError(''); }}
                            >Вход</button>
                            <button 
                                type="button"
                                className={`login-toggle ${!isLoginMode ? 'login-toggle--active' : ''}`}
                                onClick={() => { setIsLoginMode(false); setEmailError(''); }}
                            >Регистрация</button>
                        </div>
                        
                        {emailError && <div className="form-error">{emailError}</div>}
                        {pendingVerificationEmail && (
                            <div className="login-forgot-row">
                                <button
                                    type="button"
                                    className="login-link"
                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                    onClick={() => navigate(`/verify-email?email=${encodeURIComponent(pendingVerificationEmail)}`)}
                                >
                                    Подтвердить email
                                </button>
                            </div>
                        )}
                        
                        <form onSubmit={handleEmailSubmit}>
                            <div className="login-form-row">
                                <label className="form-label" htmlFor="login-email">Email</label>
                                <input id="login-email" className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                                {!isLoginMode && !invitedEmail && (
                                    <p className="login-helper" style={{ marginTop: 'var(--space-2)', marginBottom: 0 }}>
                                        {REGISTRATION_EMAIL_DOMAIN_HINT}
                                    </p>
                                )}
                            </div>
                            
                            <div className="login-form-row">
                                <label className="form-label" htmlFor="login-password">Пароль</label>
                                <input id="login-password" className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
                            </div>
                            
                            {isLoginMode && (
                                <div className="login-forgot-row">
                                    <Link to="/forgot-password" className="login-link">Забыли пароль?</Link>
                                </div>
                            )}

                            {!isLoginMode && (
                                <div className="login-checkbox-block">
                                    <div className="login-checkbox-row">
                                        <input
                                            type="checkbox"
                                            id="terms"
                                            checked={agreedToTerms}
                                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                                        />
                                        <label htmlFor="terms">
                                            Я ознакомлен и согласен с{' '}
                                            <a href="http://svod.pro/oferta" target="_blank" rel="noopener noreferrer" className="login-link">публичной офертой на оказание информационных услуг</a>
                                        </label>
                                    </div>
                                    <div className="login-checkbox-row">
                                        <input
                                            type="checkbox"
                                            id="newsletter"
                                            checked={agreedToNewsletter}
                                            onChange={(e) => setAgreedToNewsletter(e.target.checked)}
                                        />
                                        <label htmlFor="newsletter">
                                            Я согласен на получение{' '}
                                            <Link to="/mailing-consent" target="_blank" className="login-link">информационной рассылки</Link>
                                        </label>
                                    </div>
                                </div>
                            )}

                            <button type="submit" disabled={isEmailLoading} className="btn btn--primary" style={{ width: '100%' }}>
                                {isEmailLoading ? 'Загрузка...' : (isLoginMode ? 'Войти' : 'Зарегистрироваться')}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Login;
